import { ContentHeader, ContentLayout, Loader, TOP_MENU_HEIGHT } from 'Molecules';
import { useGetDRGroupData } from '../DisclosureRequirements.hooks';
import { DisclosureRequirementHeader } from './StandardPageComponents/DisclosureRequirementHeader';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useBusinessUnitMetricRequiredTagsPerStandardQuery,
  useCompanyMetricRequiredTagsPerStandardQuery,
  useShortEsrsAssessmentQuery,
  useGetBuDisclosureRequirementMetricsQuery,
  useGroupMetricRequiredTagsPerStandardQuery,
  useGetBumfDisclosureRequirementMetricsQuery,
  useGetDisclosureRequirementsOwnerQuery,
  GetBuDisclosureRequirementMetricsQuery_,
} from 'models';
import { Box, HStack, VStack } from '@chakra-ui/react';
import { DisclosureRequirementTable } from './StandardPageComponents/DisclosureRequirementsTable';
import {
  useAssessmentReportingUnits,
  useMaterialStandardId,
} from 'containers/Esrs/EsrsAssessment.hooks';
import React, { useMemo, useState } from 'react';
import { Button, Infobox } from 'Atoms';
import { SettingsIcon } from 'Tokens/Icons/Function';
import { useCurrentCompanyId } from 'utils/hooks';
import { DREnums } from '../..';
import { Typography } from 'Tokens';
import { ArrowUpRightIcon } from 'Tokens/Icons/Direction';
import { DataCollectionLevel } from '../../DataCollection';
import { FrameworkRefs } from '../../Assessment/SustainabilityAssessment.d';
import { TaskToolbar } from 'Molecules/TaskToolbar';
import { useHandleTaskUpdate } from '../Metrics/Metrics';
import { getStandardDatapoints } from './Standards.hooks';

export const StandardPage = () => {
  const navigate = useNavigate();
  const { companyId } = useCurrentCompanyId();
  const [selectedDrs, setSelectedDrs] = useState<string[]>([]);
  const { esrsAssessmentId = '', reportingUnitId, framework = '', standardRef = '' } = useParams();

  const { standard, sortedRequirements, isMFStandard, loading } = useGetDRGroupData();
  const { companyAssessmentId: companyStandardId, parentAssessmentId: parentStandardId } =
    useMaterialStandardId(standard?.reference ?? '', esrsAssessmentId ?? '');

  const { metrics: allDpRows, loading: dpRowsLoading } = getStandardDatapoints(
    companyStandardId,
    parentStandardId
  );

  const isBusinessUnit = useMemo(() => !!reportingUnitId, [reportingUnitId]);

  const { data: esrsAssessmentData, loading: assessmentLoading } = useShortEsrsAssessmentQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });

  const { reportingUnitAssessments, loading: ruLoading } =
    useAssessmentReportingUnits(esrsAssessmentId);
  const companyLevelReportingUnitId = useMemo(
    () => reportingUnitAssessments.find((ru) => ru.isCompanyLevel)?.id ?? '',
    [reportingUnitAssessments]
  );

  const currentReportingUnitId = useMemo(
    () => (isBusinessUnit ? reportingUnitId : companyLevelReportingUnitId),
    [isBusinessUnit, reportingUnitId, companyLevelReportingUnitId]
  );

  const { data: drOwnerData, loading: drOwnerLoading } = useGetDisclosureRequirementsOwnerQuery({
    variables: {
      reportingUnitId: currentReportingUnitId,
      drRefs: sortedRequirements.flatMap((req) => req.slice(1).map((rq) => rq.reference)),
      materialStandardId: companyStandardId,
    },
    fetchPolicy: 'network-only',
    skip: !sortedRequirements.length || !currentReportingUnitId || !companyStandardId,
  });

  const handleTaskUpdate = useHandleTaskUpdate(
    allDpRows?.filter((row) => selectedDrs.includes(row.disclosureRequirementRef ?? '')),
    esrsAssessmentData?.esrsAssessment?.projectLeader?.id,
    companyLevelReportingUnitId,
    selectedDrs,
    companyStandardId,
    isMFStandard
  );

  const isGroup = useMemo(
    () => esrsAssessmentData?.esrsAssessment?.company?.isGroupOwner ?? false,
    [esrsAssessmentData]
  );

  const { data: normalBuData } = useGetBuDisclosureRequirementMetricsQuery({
    variables: {
      standardRef: standard?.reference ?? '',
      companyStandardId,
      parentStandardId: parentStandardId || companyStandardId,
    },
    skip: !companyStandardId || isMFStandard,
  });

  const { data: mfBUData } = useGetBumfDisclosureRequirementMetricsQuery({
    variables: {
      mfStandardRef: standard?.reference ?? '',
      materialStandardId: companyStandardId,
      parentStandardId: parentStandardId || companyStandardId,
    },
    skip: !companyStandardId || !isMFStandard,
  });

  const buData = useMemo(() => {
    const transformedBuData: GetBuDisclosureRequirementMetricsQuery_ | undefined = {
      requirements: [],
    };
    normalBuData?.requirements.forEach((req) => {
      if (
        req.group?.standardRef === standardRef &&
        !transformedBuData.requirements.find((r) => r.reference === req.reference)
      ) {
        transformedBuData.requirements.push(req);
      } else {
        req.metrics.forEach((met) =>
          met.relatedDRs.forEach((relDr) => {
            if (
              relDr.disclosureRequirement &&
              !transformedBuData.requirements.find(
                (r) => r.reference === relDr.disclosureRequirement?.reference
              )
            ) {
              transformedBuData.requirements.push(
                relDr.disclosureRequirement as GetBuDisclosureRequirementMetricsQuery_['requirements'][0]
              );
            }
          })
        );
      }
    });

    return isMFStandard ? mfBUData : transformedBuData;
  }, [isMFStandard, mfBUData, normalBuData]);

  const { data: groupStandardTagsData } = useGroupMetricRequiredTagsPerStandardQuery({
    variables: {
      materialStandardId: companyStandardId,
    },
    skip: !isGroup || !companyStandardId || isBusinessUnit,
    fetchPolicy: 'network-only',
  });

  const { data: companyStandardTagsData } = useCompanyMetricRequiredTagsPerStandardQuery({
    variables: {
      materialStandardId: companyStandardId,
    },
    skip: isGroup || !companyStandardId || isBusinessUnit,
    fetchPolicy: 'network-only',
  });

  const { data: businessUnitStandardTagsData } = useBusinessUnitMetricRequiredTagsPerStandardQuery({
    variables: {
      materialStandardId: companyStandardId,
      parentStandardId: parentStandardId || companyStandardId,
    },
    skip: isGroup || !companyStandardId || !isBusinessUnit,
    fetchPolicy: 'network-only',
  });

  const standardTagsData = useMemo(() => {
    if (isGroup) {
      return groupStandardTagsData;
    }
    if (isBusinessUnit) {
      return businessUnitStandardTagsData;
    }
    return companyStandardTagsData;
  }, [
    isGroup,
    isBusinessUnit,
    groupStandardTagsData,
    companyStandardTagsData,
    businessUnitStandardTagsData,
  ]);
  const [drRefsMissingTagsConfiguration, setDrRefsMissingTagsConfiguration] = useState<string[]>(
    []
  );
  const dataCollectionLevel = useMemo(
    () => (isGroup ? DataCollectionLevel.subsidiaries : DataCollectionLevel.reportingUnits),
    [isGroup]
  );
  const someTagsNotConfigured = useMemo(() => {
    const materialMetrics = standardTagsData?.esrs_MaterialMetric;
    let hasMissingTags = false;
    materialMetrics?.forEach((mm) => {
      if (
        mm.childrenMetrics_aggregate &&
        mm.childrenMetrics_aggregate?.aggregate?.count === 0 &&
        mm.dataCollection &&
        mm.dataCollection === dataCollectionLevel
      ) {
        return false;
      }

      mm.metric.requiredTags.some((requiredTag) => {
        const matchingTag = mm.materialMetricTags?.find((tag) => tag.tagType === requiredTag.type);
        if (matchingTag === undefined) {
          const tagRef = mm.metric.disclosureRequirementRef;
          setDrRefsMissingTagsConfiguration((prevDrRefs) =>
            prevDrRefs.includes(tagRef) ? prevDrRefs : [...prevDrRefs, tagRef]
          );
          hasMissingTags = true;
        }
        return !matchingTag;
      });
    });
    return hasMissingTags;
  }, [standardTagsData]);
  const buDisclosureRequirements = useMemo(
    () =>
      [...(buData?.requirements ?? [])]
        .sort((a, b) => {
          if (a.order !== b.order) {
            return a.order - b.order;
          }
          return a.reference.localeCompare(b.reference);
        })
        .filter((dr) =>
          isMFStandard ? true : 'group' in dr && dr.group?.standardRef === standardRef
        ),
    [buData]
  );

  const disclosureRequirementsRefs = useMemo(
    () =>
      isBusinessUnit
        ? buDisclosureRequirements
            .filter((dr) => dr.type !== DREnums.action && dr.type !== DREnums.target)
            .map((dr) => dr.reference)
        : sortedRequirements.flatMap((sr) =>
            sr
              .slice(1)
              .filter((dr: any) => dr?.type !== DREnums.action && dr?.type !== DREnums.target)
              .map((dr) => dr.key.split('_')[1])
          ),
    [isBusinessUnit, buDisclosureRequirements, sortedRequirements]
  );

  const allDisclosureRequirementsRefs = useMemo(
    () =>
      isBusinessUnit
        ? buDisclosureRequirements.map((dr) => dr.reference)
        : sortedRequirements.flatMap((sr) => sr.slice(1).map((dr) => dr.key.split('_')[1])),
    [isBusinessUnit, buDisclosureRequirements, sortedRequirements]
  );
  const selectOptions = [
    {
      id: 'all',
      title: 'All disclosure requirements',
      onClick: () =>
        setSelectedDrs([
          ...sortedRequirements.flatMap((req) => req.slice(1).map((rq) => rq.reference)),
        ]),
    },
  ];

  if (loading || assessmentLoading) return <Loader />;

  return (
    <ContentLayout
      backButton
      header={
        <HStack w="100%" justifyContent="space-between" alignItems="start">
          <ContentHeader
            title={standard?.title}
            subtitle={framework !== FrameworkRefs.vsme ? standard?.reference : ''}
          />
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<SettingsIcon />}
            onClick={() => {
              navigate(
                `/${companyId}/sustainability-reporting/${framework}/${esrsAssessmentId}/settings/data-collection/${standard?.reference}`
              );
            }}
          >
            Settings
          </Button>
        </HStack>
      }
      variant="narrow"
    >
      <HStack w="100%" alignItems="start" spacing="16px">
        <VStack
          alignItems="stretch"
          gap="28px"
          mt="24px"
          paddingBottom={selectedDrs.length ? '40px' : ''}
        >
          <DisclosureRequirementHeader
            isGroup={esrsAssessmentData?.esrsAssessment?.company.isGroupOwner ?? false}
            owner={
              esrsAssessmentData?.esrsAssessment?.materialStandards
                .find((std) => std.standardRef === standardRef)
                ?.materialStandardOwners.find((s) => s.reportingUnitId === currentReportingUnitId)
                ?.owner
            }
            companyLevelReportingUnitId={companyLevelReportingUnitId}
            selectedRows={allDpRows ?? []}
            disclosureRequirementsRefs={disclosureRequirementsRefs}
            parentAssessmentId={esrsAssessmentData?.esrsAssessment?.parentAssessment?.id}
            requirements={isBusinessUnit ? buDisclosureRequirements : sortedRequirements}
            materialStandardId={companyStandardId}
            isMfStandard={isMFStandard}
            allDisclosureRequirementsRefs={allDisclosureRequirementsRefs}
            dpRowsLoading={dpRowsLoading}
          />
          {someTagsNotConfigured && (
            <Infobox
              status="warning"
              closable={false}
              title="Some data points are misconfigured"
              description="Some data points in this standard were not set up correctly. Please go to the standard setup to see details and to complete the required configurations."
              extra={
                <HStack
                  onClick={() => {
                    navigate(
                      `/${companyId}/sustainability-reporting/${framework}/${esrsAssessmentId}/settings/data-collection/${standard?.reference}`
                    );
                  }}
                  cursor="pointer"
                >
                  <Typography fontSize="14px" fontWeight={'500'} spacing={0} variant="body">
                    Complete setup
                  </Typography>
                  <ArrowUpRightIcon />
                </HStack>
              }
            />
          )}
          {isBusinessUnit ? (
            <DisclosureRequirementTable
              title={'Metrics'}
              selectedDrs={selectedDrs}
              companyLevelReportingUnitId={companyLevelReportingUnitId}
              drOwnerData={drOwnerData?.DisclosureRequirementOwner}
              setSelectedDrs={setSelectedDrs}
              datapointsTaskData={allDpRows ?? []}
              requirements={buDisclosureRequirements}
              owner={esrsAssessmentData?.esrsAssessment?.projectLeader}
              isGroup={esrsAssessmentData?.esrsAssessment?.company.isGroupOwner ?? false}
              parentAssessmentId={esrsAssessmentData?.esrsAssessment?.parentAssessment?.id}
              drRefsMissingTagsConfiguration={drRefsMissingTagsConfiguration}
              dpDataLoading={dpRowsLoading}
              drOwnerLoading={ruLoading || drOwnerLoading}
            />
          ) : (
            sortedRequirements.map((requirements) => (
              <DisclosureRequirementTable
                title={requirements[0].title}
                companyLevelReportingUnitId={companyLevelReportingUnitId}
                selectedDrs={selectedDrs}
                setSelectedDrs={setSelectedDrs}
                drOwnerData={drOwnerData?.DisclosureRequirementOwner}
                datapointsTaskData={allDpRows ?? []}
                requirements={requirements.slice(1)}
                owner={esrsAssessmentData?.esrsAssessment?.projectLeader}
                isGroup={esrsAssessmentData?.esrsAssessment?.company.isGroupOwner ?? false}
                parentAssessmentId={esrsAssessmentData?.esrsAssessment?.parentAssessment?.id}
                drRefsMissingTagsConfiguration={drRefsMissingTagsConfiguration}
                dpDataLoading={dpRowsLoading}
                drOwnerLoading={ruLoading || drOwnerLoading}
              />
            ))
          )}
        </VStack>

        {!!selectedDrs.length && (
          <TaskToolbar
            selectOptions={selectOptions}
            selectedText={`${selectedDrs.length} disclosure requirement${selectedDrs.length > 1 ? 's' : ''} selected`}
            handleTaskUpdate={handleTaskUpdate}
            onClose={() => setSelectedDrs([])}
          />
        )}
      </HStack>
    </ContentLayout>
  );
};
